/**
 * DASHBOARD-REPARTITION.WIDGET
 * Repartition of users for a survey
 */
import React, { useState } from "react"
import { Trans, withTranslation, WithTranslation } from "react-i18next"
import Card from "@/components/card"
import DashboardRepartitionChartWidget from "./dashboard-repartition-chart.widget"
import HelpModal from "@/components/modal-help"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import Link from "@/components/link"
import { connect } from "react-redux"
import { Session } from "@/redux/_session.types"
import { TopicState } from "@/redux/topic.types"
import { SurveyState } from "@/redux/survey.types"
import { flatten, uniq } from "lodash"
import { useNavigate } from "react-router-dom"

interface StateProps {
  _session: Session
  survey: SurveyState
  topic: TopicState
}

interface OwnProps {
  isOverview?: boolean
  isReport?: boolean
}

type Props = WithTranslation & OwnProps & StateProps

function DashboardRepartitionWidget(props: Props) {
  const { t } = props
  const navigate = useNavigate()

  const [isModalHelpOpen, setIsModalHelpOpen] = useState<boolean>(false)

  const hasRps = props.topic.list.some((tp) => tp.countRps > 0)

  let responseCountListLabels: string[][] = []

  const responseCountList = uniq(
    flatten(props.topic.list.map((x) => x.Questions.filter((x) => x.type === "scale").map((x) => x.responseCount)))
  )

  if (responseCountList.includes(5)) {
    responseCountListLabels = [
      [t("question_likert_1"), "0"],
      [t("question_likert_2"), "2.5"],
      [t("question_likert_3"), "5"],
      [t("question_likert_4"), "7.5"],
      [t("question_likert_5"), "10"]
    ]
  } else {
    responseCountListLabels = [
      [t("question_likert_1"), "0"],
      [t("question_likert_2"), "2.5"],
      [t("question_likert_4"), "7.5"],
      [t("question_likert_5"), "10"]
    ]
  }

  if (responseCountList.includes(2)) {
    responseCountListLabels = responseCountListLabels.concat([
      [t("utils_yes"), "10"],
      [t("utils_no"), "0"]
    ])
  }

  function helpTexts() {
    return (
      <div>
        <h2 className="text-primary font-bold">{t("utils_help_repartition_h1_1")}</h2>
        <p>{t("utils_help_repartition_p_1")}</p>
        <p>{t("utils_help_repartition_p_2")}</p>
      </div>
    )
  }

  return props.isOverview ? (
    <div>
      <DashboardRepartitionChartWidget isReport={props.isReport} />
      <div
        style={{
          transform: "scale(0.65)",
          transformOrigin: "top left"
        }}>
        {helpTexts()}
      </div>
    </div>
  ) : (
    <>
      <Card className={hasRps ? "_hover flex1" : "flex1"} exportName={t("survey_repartition")} title={t("survey_repartition")} onClick={hasRps ? () => navigate("/dashboard/rps") : undefined}>
        <div className="absolute top-[14px] right-6 text-sm">
          <Link icon={faInfoCircle} onClick={(e: React.MouseEvent) => { e.stopPropagation(); setIsModalHelpOpen(true) }}>
            {t("utils_help")}
          </Link>
        </div>
        <DashboardRepartitionChartWidget isReport={props.isReport} />
      </Card>
      {isModalHelpOpen && (
        <HelpModal title={t("survey_repartition")} onClose={() => setIsModalHelpOpen(false)}>
          <div className="max-w-xl mx-auto bg-white">
            {helpTexts()}

            <h3 className="mt-6 font-semibold text-gray-700">{t("utils_help_repartition_h2_1")}</h3>
            <table className="w-full border border-gray-300 mt-4">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">{t("question_response")}</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">{t("utils_help_repartition_th_1")}</th>
                </tr>
              </thead>
              <tbody>
                {responseCountListLabels.map(([label, points]) => (
                  <tr key={label} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{label}</td>
                    <td className="border border-gray-300 px-4 py-2">{points}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2 className="text-primary font-bold">{t("utils_help_cs_h2_2")}</h2>
            <p>{t("utils_help_cs_p_2")}</p>
            <h3 className="mt-6 font-semibold text-gray-700">{t("utils_help_cs_h3_1")}</h3>
            <ul>
              <li>{<Trans>{t("utils_help_cs_li_6")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_7")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_8")}</Trans>}</li>
            </ul>

            <h2 className="text-primary font-bold mt-10">{t("utils_help_cs_h2_3")}</h2>
            <p>{t("utils_help_cs_p_3")}</p>

            <h3 className="mt-6 font-semibold text-gray-700">{t("utils_help_cs_h3_1")}</h3>
            <ul>
              <li>{<Trans>{t("utils_help_cs_li_9")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_10")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_11")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_12")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_13")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_14")}</Trans>}</li>
            </ul>

            <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 mt-6 rounded-md">
              <p className="font-medium">{t("utils_help_cs_p_4")}</p>
              <p>{t("utils_help_cs_p_5")}</p>
            </div>

            {props.survey.active.template.options.isGollac && (
              <div>
                <h2>Évaluation Gollac</h2>
                <p></p>Cette répartition suit les recommandations du modèle Gollac, utilisé en prévention des RPS :
                <h3>1 à 6 → exposition / vigilance</h3>
                <p>
                  Scores considérés comme négatifs ou insuffisamment positifs : ils indiquent une fragilité potentielle
                  ou un facteur de risque.
                </p>
                <h3>7 à 10 → zone sécurisée</h3>
                <p>
                  À partir de 7, la perception devient réellement positive et stable, signe d’un environnement ressenti
                  comme protecteur.
                </p>
                <p>
                  C’est une interprétation standard dans les démarches RPS (ANACT, INRS, cabinets spécialisés) et elle
                  permet d’identifier les signaux faibles avant que la situation ne se dégrade.
                </p>
              </div>
            )}
          </div>
        </HelpModal>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(DashboardRepartitionWidget))
